import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useWishlist } from "@/contexts/WishlistContext";
import WishlistCard from "@/components/product/WishlistCard";

const CATEGORY_CHIPS = ["All", "Medicines", "Vitamins & Supplements", "Skincare", "Baby Care", "Devices", "Health Food"];
const SORT_OPTIONS = [
  { value: "recent", label: "Recently Added" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

export default function AccountWishlist() {
  const { items, count } = useWishlist();
  const [chip, setChip] = useState("All");
  const [sort, setSort] = useState("recent");

  const sorted = useMemo(() => {
    const cp = [...items];
    if (sort === "price-asc") cp.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sort === "price-desc") cp.sort((a, b) => (b.price || 0) - (a.price || 0));
    return cp;
  }, [items, sort]);

  return (
    <div data-testid="account-wishlist" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Wishlist
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Your saved products, ready when you are.
        </p>
      </header>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {CATEGORY_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setChip(c)}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                chip === c
                  ? "bg-[#4B2AAD] text-white border-[#4B2AAD]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-[#4B2AAD] hover:text-[#4B2AAD]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          Sort by:
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1 bg-white outline-none focus:border-[#4B2AAD]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {count === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center">
          <div className="h-14 w-14 rounded-2xl bg-[#F3EFFC] flex items-center justify-center mx-auto">
            <Heart className="h-6 w-6 text-[#4B2AAD]" />
          </div>
          <h2 className="mt-3 font-display text-xl font-bold text-gray-900">
            Your wishlist is empty
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Tap the heart on any product to save it here for later.
          </p>
          <Link to="/shop" className="dyntra-btn-primary mt-5 inline-flex">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4">
          {sorted.map((p) => (
            <WishlistCard key={p.id} product={p} />
          ))}
        </div>
      )}

      <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 text-xs text-gray-700">
        Prices and availability may change. Add items to cart to lock in your selections.
      </div>
    </div>
  );
}
